import iconsPng from '@/assets/img/png/icons';
import svgIcons from '@/assets/svg/icons';
import CustomButton from '@/components/atoms/CustomButton';
import SvgIcon from '@/components/atoms/SvgIcon';
import WellnessStateCircleMobile from '@/components/molecules/WellnessStateCircle/WellnessStateCircleMobile';
import MobileFirstPageTemplate from '@/components/templates/MobileFirstPageTemplate';
import { EMPTY_WELLNESS_DOMAINS } from '@/constants';
import { MOBILE_DEFAULT_PADDING } from '@/constants/ui';
import useIsWeb from '@/hooks/useIsWeb';
import TryScreenerOrganizationScore from '@/modules/try-screener/TryScreenerOrganizationScore';
import TryScreenerOrganizationValidation from '@/modules/try-screener/TryScreenerOrganizationValidation';
import { usePublicScreenerStore } from '@/store/usePublicScreenerStore';
import colors from '@/theme/colors';
import { Center, HStack, Image, Text, useDisclosure, useToast, VStack } from '@chakra-ui/react';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import SendDetailedReportToEmailModal from '@/modules/try-screener/SendDetailedReportToEmailModal';
import useTelemetry from '@/providers/TelemetryProvider/useTelemetry';
import { mixpanelEvents, PUBLIC_SCREENER_TYPE } from '@/constants/mixpanel';

const TryScreenerSuccessPage = () => {
  const { trackEvent } = useTelemetry();

  const { organizationSlug } = Route.useParams();
  const { submissionId } = Route.useSearch();

  useEffect(() => {
    trackEvent(mixpanelEvents.SCREENER_RESULTS_VIEWED, { type: PUBLIC_SCREENER_TYPE });
  }, []);

  const toast = useToast();
  const isWeb = useIsWeb();

  const {
    isOpen: isSendToEmailModalOpen,
    onOpen: onSendToEmailModalOpen,
    onClose: onSendToEmailModalClose,
  } = useDisclosure();

  const { result: publicScreenerResult } = usePublicScreenerStore();
  const [isOrganizationValid, setIsOrganizationValid] = useState<boolean>(false);

  const appUrl = `${window.location.origin}`;
  const publicScreenerUrl = `/try-screener/${organizationSlug}`;
  const shareLink = `${appUrl}${publicScreenerUrl}?source=shared`;

  const [copied, setCopied] = useState(false);
  const onCopyUrlToClipboard = () => {
    setCopied(true);

    toast({
      title: 'URL copied to clipboard',
      description: shareLink,
      status: 'success',
    });

    setTimeout(() => setCopied(false), 5000);
  };

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (isOrganizationValid) {
      timer = setTimeout(() => {
        onSendToEmailModalOpen();
      }, 20000); // 20 seconds
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [isOrganizationValid]);

  const onShareScreenerClick = useCallback(() => {
    navigator.share({
      url: shareLink,
      title: 'Share MyOmnia Screener',
      text: 'Try the MyOmnia Screener and see your wellness score!',
    });
  }, [isWeb]);

  const personalDomainsFinal = publicScreenerResult?.scores;
  const personalTotalScoreFinal = publicScreenerResult?.totalScore;

  if (!isOrganizationValid) {
    return (
      <MobileFirstPageTemplate>
        <TryScreenerOrganizationValidation
          isValid={isOrganizationValid}
          organizationSlug={organizationSlug as string}
          setIsValid={setIsOrganizationValid}
        />
      </MobileFirstPageTemplate>
    );
  }

  return (
    <MobileFirstPageTemplate>
      <VStack position={'relative'} maxWidth={'100%'} width={'100%'} overflow={'hidden'}>
        <Center
          position={'fixed'}
          bottom={8}
          zIndex={5}
          px={MOBILE_DEFAULT_PADDING}
          width={'100%'}
          alignItems={'center'}
          justifyContent={'center'}
        >
          <Center style={{ width: '100%' }}>
            <CustomButton
              backgroundColor="primary.500"
              labelColor="extra.white"
              label="Share the Screener"
              onClick={onShareScreenerClick}
              width={'100%'}
              style={{
                maxWidth: '360px',
              }}
            />
          </Center>
        </Center>
        <Text variant={'urbanistSemiBoldSmallTitle'} mt={'10px'}>
          Kudos for completing the screener!
        </Text>
        <Center marginTop={'20px'}>
          <Image alt="add-member" src={iconsPng.handshake} width={100} height={100} />
        </Center>
        <Text variant={'loraExtraSmallTitle'} marginY={'20px'}>
          Your Personal Wholeness Ring
        </Text>
        <WellnessStateCircleMobile
          domainsData={personalDomainsFinal ?? EMPTY_WELLNESS_DOMAINS}
          totalScore={personalTotalScoreFinal ?? 0}
          showPercentageTextOnTopOfCircle
          circleSvgId="personal-circle-svg"
        />
        <Text
          variant={'urbanistSemiBold'}
          color={'extra.white'}
          backgroundColor={'secondary.500'}
          padding={'12px'}
          borderRadius={'8px'}
          maxWidth={'360px'}
          mt={'40px'}
        >
          Your results have been added to your organization’s MyOmnia Wholeness Ring.
        </Text>

        <TryScreenerOrganizationScore
          organizationSlug={organizationSlug}
          isOrganizationValid={isOrganizationValid}
          showCtaButton={false} // No need for CTA button here, as we are already on the success page
        />
        <Text variant={'loraExtraSmallTitle'} maxWidth={'260px'} marginY={'30px'}>
          Share the Screener with your Co-Workers
        </Text>
        <HStack
          borderRadius={'8px'}
          border={'border: 1px solid #0025484D'}
          backgroundColor={'extra.white'}
          boxShadow="0px 2px 4px 0px #0041790D"
          justifyContent={'space-between'}
          paddingX={'18px'}
          paddingY={'11px'}
          gap={'10px'}
          marginBottom={'220px'}
          width={'100%'}
          marginX={MOBILE_DEFAULT_PADDING}
          maxWidth={'360px'}
        >
          <VStack alignItems={'start'} gap={'4px'} width={'100%'}>
            <Text variant={'urbanistExtraBoldSmall'} color={'text.mediumGray'}>
              YOUR ORGANIZATION’S UNIQUE URL
            </Text>
            <Text
              variant={'urbanistSemiBoldRegular'}
              maxWidth={'340px'}
              noOfLines={1}
              style={{ lineBreak: 'anywhere' }}
            >
              {shareLink}
            </Text>
          </VStack>
          <VStack>
            {copied ? (
              <SvgIcon
                iconPath={svgIcons.check}
                color={colors.text.mediumBlue}
                size={14}
                boxWidth={12}
                boxHeight={12}
              />
            ) : (
              <CopyToClipboard text={shareLink} onCopy={onCopyUrlToClipboard}>
                <button>
                  <SvgIcon iconPath={svgIcons.copy} color={colors.text.mediumBlue} size={18} />
                </button>
              </CopyToClipboard>
            )}
          </VStack>
        </HStack>
      </VStack>
      <SendDetailedReportToEmailModal
        submissionId={submissionId}
        isOpen={isSendToEmailModalOpen}
        onClose={onSendToEmailModalClose}
        organizationSlug={organizationSlug}
      />
    </MobileFirstPageTemplate>
  );
};

export const Route = createFileRoute('/try-screener/$organizationSlug/screener/success/')({
  staticData: { title: 'Screener Complete' },
  component: TryScreenerSuccessPage,
  validateSearch: (search: Record<string, unknown>): { submissionId?: string } => ({
    submissionId: search.submissionId as string,
  }),
});
